/**
 * Etiquetas en español de los valores crudos de Meta (la API NO los traduce y su lista no es
 * cerrada: todo tiene fallback al valor crudo). Puro.
 */

const strip = (value) => String(value == null ? '' : value).trim();

// ── Edad y género ───────────────────────────────────────────────────────────

export const META_AGE_ORDER = ['13-17', '18-24', '25-34', '35-44', '45-54', '55-64', '65+'];
export const metaAgeRank = (age) => {
  const i = META_AGE_ORDER.indexOf(strip(age));
  return i < 0 ? META_AGE_ORDER.length : i;
};
export function metaAgeLabel(age) {
  const a = strip(age);
  if (!a || a.toLowerCase() === 'unknown') return 'Sin dato de edad';
  return a;
}

const GENDERS = { female: 'Mujeres', male: 'Hombres', unknown: 'Desconocido' };
/** `female` → «Mujeres»; `null` / `unknown` / otro → «Desconocido». */
export function metaGenderLabel(gender) {
  return GENDERS[strip(gender).toLowerCase()] || GENDERS.unknown;
}
export const metaGenderKey = (gender) => (GENDERS[strip(gender).toLowerCase()] ? strip(gender).toLowerCase() : 'unknown');

// ── Países y regiones ───────────────────────────────────────────────────────

let regionNames = null;
/** `AR` → «Argentina» (nombres del navegador en es-AR); si no lo sabe, el código tal cual. */
export function metaCountryLabel(code) {
  const c = strip(code).toUpperCase();
  // `ZZ` es «región desconocida» para el navegador.
  if (!c || c === 'ZZ') return 'Sin dato de país';
  try {
    regionNames = regionNames || new Intl.DisplayNames(['es-AR'], { type: 'region' });
    const name = regionNames.of(c);
    return name && name !== c ? name : c;
  } catch {
    return c;
  }
}

/** Sin tildes, minúsculas, sin «province / provincia de» ni signos: clave para cruzar nombres. */
export function metaNormalizePlace(value) {
  return strip(value)
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\bprovincia de\b|\bprovince of\b|\bprovincia\b|\bprovince\b/g, '')
    .replace(/[^a-z0-9 ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Las 24 jurisdicciones argentinas (clave normalizada → nombre). Los textos exactos que manda Meta
// no se pudieron verificar con datos reales: ajustar esta tabla con los primeros datos de DEV.
const AR_REGIONS = [
  'Buenos Aires', 'Catamarca', 'Chaco', 'Chubut', 'Córdoba', 'Corrientes', 'Entre Ríos', 'Formosa', 'Jujuy',
  'La Pampa', 'La Rioja', 'Mendoza', 'Misiones', 'Neuquén', 'Río Negro', 'Salta', 'San Juan', 'San Luis',
  'Santa Cruz', 'Santa Fe', 'Santiago del Estero', 'Tierra del Fuego', 'Tucumán',
].reduce((acc, name) => ({ ...acc, [metaNormalizePlace(name)]: name }), {});
const CABA = 'Ciudad de Buenos Aires';
[
  'ciudad autonoma de buenos aires', 'ciudad de buenos aires', 'buenos aires f d', 'buenos aires fd',
  'buenos aires federal district', 'buenos aires city', 'capital federal', 'caba', 'autonomous city of buenos aires',
].forEach((k) => { AR_REGIONS[k] = CABA; });
AR_REGIONS['tierra del fuego antartida e islas del atlantico sur'] = 'Tierra del Fuego';

/** «Cordoba», «Córdoba Province» → «Córdoba»; si no la conoce, el texto crudo. */
export function metaRegionLabel(region) {
  const raw = strip(region);
  if (!raw) return 'Sin dato de región';
  return AR_REGIONS[metaNormalizePlace(raw)] || raw;
}

// ── Plataformas y ubicaciones ───────────────────────────────────────────────

const PLATFORMS = {
  facebook: 'Facebook', instagram: 'Instagram', messenger: 'Messenger', audience_network: 'Audience Network',
  threads: 'Threads', whatsapp: 'WhatsApp',
};
export function metaPlatformLabel(platform) {
  const p = strip(platform);
  if (!p) return 'Sin dato de plataforma';
  return PLATFORMS[p.toLowerCase()] || p;
}

// Ubicación sin el prefijo de la plataforma (`instagram_reels` → `reels`).
const POSITIONS = {
  feed: 'Feed', story: 'Historias', stories: 'Historias', reels: 'Reels', reels_overlay: 'Reels', explore: 'Explorar',
  explore_home: 'Inicio de Explorar', profile_feed: 'Feed del perfil', search: 'Búsqueda', video_feeds: 'Videos',
  right_hand_column: 'Columna derecha', marketplace: 'Marketplace', instream_video: 'Video dentro de otros videos',
  an_classic: 'Red de Audiencia (clásico)', rewarded_video: 'Video con recompensa', messenger_inbox: 'Bandeja de Messenger',
  inbox: 'Bandeja de entrada', threads_stream: 'Feed de Threads', stream: 'Feed', facebook_reels: 'Reels',
  home: 'Inicio', biz_disco_feed: 'Feed de descubrimiento', ig_search: 'Búsqueda',
};
function positionName(position, platform) {
  let p = strip(position).toLowerCase();
  if (!p) return '';
  const plat = strip(platform).toLowerCase();
  if (plat && p.startsWith(`${plat}_`)) p = p.slice(plat.length + 1);
  else {
    const m = /^(facebook|instagram|messenger|threads)_(.+)$/.exec(p);
    if (m) p = m[2];
  }
  return POSITIONS[p] || strip(position);
}

/** Solo la ubicación («Historias»), o '' si no hay. */
export const metaPositionName = positionName;

/** «Instagram · Reels»; sin ubicación solo la plataforma; valores desconocidos, crudos. */
export function metaPlacementLabel(platform, position) {
  const plat = metaPlatformLabel(platform);
  const pos = positionName(position, platform);
  return pos ? `${plat} · ${pos}` : plat;
}

// ── Presupuesto ─────────────────────────────────────────────────────────────

const BID_STRATEGIES = {
  LOWEST_COST_WITHOUT_CAP: 'Menor costo',
  LOWEST_COST_WITH_BID_CAP: 'Menor costo con límite de puja',
  COST_CAP: 'Límite de costo',
  LOWEST_COST_WITH_MIN_ROAS: 'Menor costo con retorno mínimo',
};
export function metaBidStrategyLabel(value) {
  const v = strip(value);
  return v ? (BID_STRATEGIES[v.toUpperCase()] || v) : '';
}

const BUYING_TYPES = { AUCTION: 'Subasta', RESERVED: 'Reservado' };
export function metaBuyingTypeLabel(value) {
  const v = strip(value);
  return v ? (BUYING_TYPES[v.toUpperCase()] || v) : '';
}

const ADSET_STATUSES = {
  ACTIVE: 'Activo', PAUSED: 'Pausado', CAMPAIGN_PAUSED: 'Campaña pausada', ADSET_PAUSED: 'Pausado',
  IN_PROCESS: 'En proceso', WITH_ISSUES: 'Con problemas', ARCHIVED: 'Archivado', DELETED: 'Eliminado',
  PENDING_REVIEW: 'En revisión', DISAPPROVED: 'Rechazado', PREAPPROVED: 'Preaprobado',
  PENDING_BILLING_INFO: 'Falta información de pago',
};
/** Estado de un conjunto de anuncios (`effective_status`). */
export function metaAdSetStatusLabel(value) {
  const v = strip(value);
  return v ? (ADSET_STATUSES[v.toUpperCase()] || v) : '';
}
